import { createFileRoute } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

import { AdminGate } from "@/components/admin/AdminGate";
import { GolpePainel } from "@/components/admin/guard/golpe-modulos";

export const Route = createFileRoute("/inteligencia-golpe")({
  head: () => ({
    meta: [
      { title: "Inteligência Golpe · Segurança do Lançamento FEO" },
      { name: "description", content: "Estudo interno do golpe com checkout falso e protocolo de segurança do lançamento Black 2026 da FEO." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Inteligência Golpe · FEO" },
      { property: "og:description", content: "Dossiê do golpe e plano de segurança do lançamento da FEO." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: () => (
    <AdminGate route="/inteligencia-golpe">
      <Page />
    </AdminGate>
  ),
});

function Sec({ t, sub, children }: { t: string; sub?: string; children: ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="text-xl font-bold text-foreground">{t}</h2>
      {sub && <p className="mt-1 text-sm text-muted-foreground">{sub}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`rounded-xl border border-border bg-card p-4 ${className}`}>{children}</div>;
}

function Row({ k, v, nota }: { k: string; v: ReactNode; nota?: string }) {
  return (
    <div className="grid gap-1 border-b border-border py-2 last:border-0 md:grid-cols-[220px_1fr]">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{k}</div>
      <div className="break-all text-sm text-foreground">
        {v}
        {nota && <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">{nota}</span>}
      </div>
    </div>
  );
}

function Copiar({ titulo, texto }: { titulo: string; texto: string }) {
  const [ok, setOk] = useState(false);
  return (
    <Card>
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold">{titulo}</h3>
        <button
          className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground"
          onClick={() => {
            navigator.clipboard.writeText(texto);
            setOk(true);
            setTimeout(() => setOk(false), 1500);
          }}
        >
          {ok ? "Copiado!" : "Copiar"}
        </button>
      </div>
      <pre className="mt-3 whitespace-pre-wrap font-sans text-sm text-muted-foreground">{texto}</pre>
    </Card>
  );
}

const PIX =
  "00020126870014br.gov.bcb.pix2565qrcode.fyhub.com.br/qr/v3/at/cd0b8875-f2c7-4d1c-b37f-0176369ae8ca5204000053039865802BR5925AMORIM_INTERMEDIACAO_LTDA6014DUQUE_DE_CAXIA62070503***6304552E";

const turnos = [
  { t: "Manhã", h: "06h às 12h", m: "Tatão 01 e Karen 01", a: "Karen 02 e Tatão 02" },
  { t: "Tarde", h: "12h às 18h", m: "Tatão 01", a: "Lucão" },
  { t: "Tarde/Noite", h: "18h às 21h", m: "Tarcísio", a: "Karen 02" },
  { t: "Noite", h: "21h às 00h", m: "Tarcísio", a: "Lucão" },
  { t: "Madrugada", h: "00h às 06h", m: "Sem plantão humano", a: "Grupo fechado + fila de denúncias", gap: true },
];

const ALERTA_GRUPO = `🚨 ALERTA IMPORTANTE · FISIO EM ORTOPEDIA

Identificamos uma tentativa de golpe envolvendo nossos grupos de WhatsApp. Terceiros estão abordando participantes no privado e usando indevidamente o nome e a foto de Lucas Nery.

O site fisio-ortopedia-checkout.lovable.app NÃO é um canal oficial da FEO. Não faça pagamentos nem informe seus dados por esse link.

O número +55 11 93410-0434 foi reportado por usar essa identidade.

A FEO não envia ofertas nem pede Pix no privado. Nossas vendedoras oficiais são: Bruna, Ingrid, Weruska, Daphne, Jana e Patrícia.

Recebeu algo suspeito? Mande o print aqui para a equipe, denuncie e bloqueie o contato.`;

const AVISO_FIXO = `⚠️ AVISO DE SEGURANÇA FEO
A equipe da FEO não aborda no privado pedindo pagamento.
Links oficiais só neste grupo ou com as vendedoras oficiais: Bruna, Ingrid, Weruska, Daphne, Jana e Patrícia.
Desconfie de Pix para empresas com outro nome e de preços muito abaixo da oferta oficial.`;

const RESP_PRIVADO = `Oi! Obrigado por avisar 🙏
Esse contato NÃO é da FEO. O link fisio-ortopedia-checkout.lovable.app é falso.
Por favor, não pague e não envie seus dados. Denuncie e bloqueie o número no WhatsApp.
A oferta oficial sai só pelo grupo e pelas nossas vendedoras oficiais.`;

const RESP_PAGOU = `Sentimos muito pelo ocorrido. Faça agora, nesta ordem:
1. Ligue para o seu banco e peça a contestação do Pix pelo MED (Mecanismo Especial de Devolução). Quanto antes, maior a chance de bloquear o valor.
2. Guarde os prints da conversa, o link e o comprovante.
3. Registre boletim de ocorrência online (Delegacia Virtual do seu estado).
4. Nos envie os prints para anexarmos ao dossiê da denúncia.`;

const EMAIL_LOVABLE = `Para: support@lovable.dev
Assunto: URGENT – Phishing / brand impersonation site hosted on Lovable (fisio-ortopedia-checkout.lovable.app)

Hello Lovable Trust & Safety team,

I am Tarcísio Rupel, from Fisio em Ortopedia (FEO), a Brazilian physiotherapy education company (https://fisioemortopedia.com.br).

A project hosted on Lovable is being used to defraud our students:

- URL: https://fisio-ortopedia-checkout.lovable.app/
- Project ID: lovp_175dnvvad59xw8k9kw8w11bprq
- Published commit: 4a22d0ac3b199c98b2f061b6bff66c285385b37d

The site clones our brand and our "Black 2x1 2026" campaign, offers a fake product ("Pós em Dobro", R$ 347,00), collects name, e-mail, phone and CPF (Brazilian tax ID) and generates a Pix payment to a third party (AMORIM INTERMEDIACAO LTDA, via FyHub). The link is being sent privately via WhatsApp to members of our official groups by someone impersonating a member of our team.

We kindly request:
1. Immediate takedown of the site and project.
2. Preservation of the account owner data and logs, so we can share them with Brazilian police upon a formal request.
3. Confirmation once the site is offline.

Evidence (screenshots, Pix code, WhatsApp messages) attached.

Thank you,
Tarcísio Rupel
Fisio em Ortopedia (FEO)
https://fisioemortopedia.com.br`;

const EMAIL_FYHUB = `Assunto: URGENTE – Conta recebedora usada em golpe (Pix) – AMORIM INTERMEDIACAO LTDA

Olá, equipe FyHub.

Somos a Fisio em Ortopedia (FEO). Uma conta da sua plataforma está recebendo Pix de um golpe com checkout falso que usa nossa marca:

- Site falso: https://fisio-ortopedia-checkout.lovable.app/
- Recebedor no Pix: AMORIM INTERMEDIACAO LTDA (Duque de Caxias/RJ)
- Cobrança: cd0b8875-f2c7-4d1c-b37f-0176369ae8ca
- Chave Pix: 61020ba4-248e-4bae-8c84-b151f6f24121
- Valor: R$ 347,00

Pedimos o bloqueio cautelar da conta, a preservação dos dados do titular e o apoio às vítimas via MED. Seguem prints em anexo.

Atenciosamente,
Tarcísio Rupel · Fisio em Ortopedia (FEO)`;

function Page() {
  return (
    <main className="dark min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-5xl px-4 py-10">
        <p className="text-sm font-semibold uppercase tracking-widest text-primary">Inteligência interna · FEO · 21/10/2026</p>
        <h1 className="mt-2 text-3xl font-extrabold md:text-4xl">Golpe do checkout falso e segurança do lançamento</h1>

        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          {[
            ["Golpe", "R$ 347", "Pix no privado"],
            ["Identidade usada", "Lucas Nery", "Vítima, não suspeito"],
            ["Recebedor", "AMORIM", "Intermediação LTDA"],
            ["Status", "No ar", "Derrubar já"],
          ].map(([l, v, d]) => (
            <Card key={l}>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">{l}</div>
              <div className="mt-1 text-2xl font-bold">{v}</div>
              <div className="mt-1 text-xs text-muted-foreground">{d}</div>
            </Card>
          ))}
        </div>

        <h2 className="mt-12 border-b border-border pb-2 text-2xl font-extrabold text-primary">Ao vivo · ações contra o golpe</h2>
        <p className="mt-2 text-sm text-muted-foreground">O robô anti-golpe dos grupos fica no FEO Guard. Aqui ficam o passo a passo para derrubar, os golpistas identificados e as ferramentas de vigilância.</p>
        <div className="mt-4"><GolpePainel /></div>

        <h2 className="mt-12 border-b border-border pb-2 text-2xl font-extrabold text-primary">Parte 1 · O golpe</h2>

        <Sec t="Resumo">
          <Card>
            <ul className="list-disc space-y-1 pl-5 text-sm">
              <li>Número +55 11 93410-0434 com nome e foto de Lucas Nery chama membros dos grupos da Black no privado.</li>
              <li>Envia um clone da nossa página com "Pós em Dobro" a R$ 347 no Pix, timer e falsas vagas.</li>
              <li>Coleta nome, e-mail, WhatsApp e CPF e gera Pix para outra empresa.</li>
            </ul>
          </Card>
        </Sec>

        <Sec t="Dados do golpista">
          <Card>
            <Row k="Site" v="https://fisio-ortopedia-checkout.lovable.app/" />
            <Row k="Projeto Lovable" v="lovp_175dnvvad59xw8k9kw8w11bprq · commit 4a22d0ac…385b37d" />
            <Row k="Recebedor Pix" v="AMORIM INTERMEDIACAO LTDA · Duque de Caxias/RJ" />
            <Row k="Intermediador" v="FyHub = KIKAI Sociedade de Crédito Direto S.A. · CNPJ 43.978.697/0001-82 · Al. Rio Negro, 500, sala 1213, Alphaville, Barueri/SP" nota="confirmado" />
            <Row k="Cobrança" v="cd0b8875-f2c7-4d1c-b37f-0176369ae8ca · txid do teste f39063193b49b9ab65e6359813185a" />
            <Row k="Chave Pix" v="61020ba4-248e-4bae-8c84-b151f6f24121" />
            <Row k="CNPJ do recebedor" v="69.582.607/0001-23 · Amorim Intermediacao LTDA · aberta 13/10/2026 (8 dias antes do golpe) · EPP · capital declarado R$ 150.000 · CNAE 74.90-1-04" nota="confirmado em base pública" />
            <Row k="Endereço do recebedor" v="Rua Joaquim Tenório, 78, Parque Felicidade, Duque de Caxias/RJ, CEP 25080-340" />
            <Row k="Contatos cadastrados" v="2 telefones fixos (21) 222…/(21) 333… e e-mail co…@….com (mascarados nas bases públicas)" />
            <Row k="Sócios" v="Ainda não aparecem nas bases públicas (empresa recém-aberta). Ver na Receita/JUCERJA ou pedir à polícia." nota="pendente" />
            <Row k="Nome no QR do teste" v="Daniel Araujo Costa (apareceu como pagador/titular na cobrança gerada)" nota="não comprovado" />
            <Row k="Sinais de fachada" v="Empresa nova, capital alto sem histórico, atividade genérica de intermediação, Pix de terceiro" />
            <Row k="WhatsApp" v="+55 11 93410-0434" />
            <Row k="Dono da conta Lovable" v="Não é público. A Lovable só revela por ordem judicial/pedido da polícia; por isso o e-mail pede para guardar os dados." nota="não comprovado" />
            <Row k="Quem mais tem os dados" v="FyHub/KIKAI (titular e sócios da conta Pix), banco de destino, Meta (dono do número do WhatsApp) e Lovable (conta e pagamentos)" />
          </Card>
          <details className="mt-3 rounded-xl border border-border bg-muted p-3 text-xs text-muted-foreground">
            <summary className="cursor-pointer font-semibold text-foreground">Código Pix copia e cola</summary>
            <code className="mt-2 block break-all">{PIX}</code>
          </details>
        </Sec>

        <Sec t="Derrubar o golpe agora" sub="Enviar hoje, nesta ordem, com prints em anexo.">
          <div className="grid gap-3">
            <Copiar titulo="1. E-mail para a Lovable (retirada do site)" texto={EMAIL_LOVABLE} />
            <Copiar titulo="2. E-mail para a FyHub (bloqueio da conta Pix)" texto={EMAIL_FYHUB} />
            <Card>
              <ol start={3} className="list-decimal space-y-1 pl-5 text-sm">
                <li><b>Bloqueio do site nos navegadores:</b> denunciar em safebrowsing.google.com/safebrowsing/report_phish e em report.netcraft.com. O Chrome passa a mostrar tela vermelha mesmo antes da Lovable tirar do ar.</li>
                <li><b>WhatsApp:</b> equipe e alunos abordados entram no contato → Denunciar e bloquear. Muitas denúncias derrubam o número.</li>
                <li><b>Pix:</b> quem pagou aciona o banco pelo MED em até 80 dias. A FEO avisa a FyHub/KIKAI e pode registrar reclamação no Banco Central.</li>
                <li><b>Polícia:</b> BO online por estelionato e falsa identidade (incluir Lucas Nery). Pedir que a polícia oficie Lovable, FyHub e Meta: é assim que se descobre o dono da conta e os sócios.</li>
                <li><b>Advogado (mais rápido):</b> pedido judicial urgente de retirada e quebra de dados contra Lovable, FyHub e Meta, com esta página como prova.</li>
                <li><b>Denúncia extra:</b> SaferNet (new.safernet.org.br/denuncie) e Procon, se houver vítimas.</li>
              </ol>
            </Card>
          </div>
        </Sec>

        <h2 className="mt-12 border-b border-border pb-2 text-2xl font-extrabold text-primary">Parte 2 · Segurança do lançamento</h2>

        <Sec t="Configuração dos grupos">
          <Card>
            <ul className="list-disc space-y-1 pl-5 text-sm">
              <li>Só admins enviam mensagens e editam nome, foto e descrição.</li>
              <li>Usar grupo de avisos da Comunidade (esconde a lista de membros).</li>
              <li>Aviso de segurança fixado e na descrição. Admins só da equipe, revisados antes de cada disparo.</li>
            </ul>
          </Card>
        </Sec>

        <Sec t="Plantão manual" sub="1 como membro (vê o que o aluno vê) e 1 como admin (age). Alertas no ALERTA GERAL (WhatsApp + Discord).">
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
                <tr><th className="p-3">Turno</th><th className="p-3">Horário</th><th className="p-3">Membro</th><th className="p-3">Admin</th></tr>
              </thead>
              <tbody>
                {turnos.map((r) => (
                  <tr key={r.t} className={`border-t border-border ${r.gap ? "text-muted-foreground" : ""}`}>
                    <td className="p-3 font-semibold">{r.t}</td>
                    <td className="p-3">{r.h}</td>
                    <td className="p-3">{r.m}</td>
                    <td className="p-3">{r.a}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Card className="mt-3">
            <p className="text-sm"><b>Viu algo suspeito:</b> membro faz print e avisa → admin apaga para todos, remove e fecha o grupo → posta o alerta oficial → todos denunciam o número.</p>
          </Card>
        </Sec>

        <Sec t="Automação do grupo">
          <div className="grid gap-3 md:grid-cols-2">
            <Card>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Opção 1 · ferramenta pronta</p>
              <h3 className="mt-1 text-lg font-bold">SendFlow, Devzapp, Grupos VIP</h3>
              <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
                <li>Chip novo só para o bot.</li>
                <li>Conectar o número na ferramenta e colocar como admin dos grupos.</li>
                <li>Ligar anti-link e remoção automática; liberar números da equipe.</li>
                <li>Testar mandando um link com um número comum.</li>
              </ol>
              <p className="mt-2 text-xs text-muted-foreground">Pronto em 1 dia. Confirmar as funções no teste grátis.</p>
            </Card>
            <Card className="border-primary">
              <p className="text-xs font-semibold uppercase tracking-widest text-primary">Opção 2 · FEO Guard (próprio)</p>
              <h3 className="mt-1 text-lg font-bold">Feito aqui no site, sem assinatura</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Regras, painel e radar feitos aqui; só a conexão do número usa um serviço externo (Z-API).
              </p>
            </Card>
          </div>
        </Sec>

        <Sec t="FEO Guard · plano de implementação" sub="Atualizado em 21/10. Já construído: regras, painel, alertas, golpe por edição, anti-impostor e radar de páginas falsas.">
          <div className="grid gap-3">
            {[
              ["Já no ar após publicar (custo zero)", [
                "Radar de páginas falsas: a cada 30 min confere ~160 variações do nome FEO e sites novos com nosso nome; avisa e gera o e-mail de denúncia.",
                "Painel FEO Guard no admin: ocorrências, simulador, lista de sites e números liberados.",
                "Regras prontas: link não oficial, Pix/pagamento, número estranho, nome imitando a equipe e mensagem editada depois de enviada.",
              ]],
              ["Passo único que falta: ligar um número ao robô", [
                "Comprar um chip só para o robô e colocar como admin de todos os grupos (nunca o número principal).",
                "Conectar esse número por QR Code no Z-API e colar o endereço do robô em Ao receber, com aviso de mensagens editadas ligado.",
                "Preencher no painel o grupo da equipe e o Discord. A partir daí apaga e remove sozinho, 24h.",
              ]],
            ].map(([t, itens]) => (
              <Card key={t as string}>
                <h3 className="font-semibold">{t as string}</h3>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                  {(itens as string[]).map((i) => <li key={i}>{i}</li>)}
                </ul>
              </Card>
            ))}
          </div>
        </Sec>

        <Sec t="Por que não a conexão direta da Meta" sub="A API oficial do WhatsApp (Cloud API) é a ligação mais direta, mas não serve para estes grupos.">
          <Card>
            <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              <li>A API oficial só gerencia grupos criados pelo próprio número oficial e, na documentação atual, com poucos participantes (até 8). Não entra nos nossos grupos grandes já existentes.</li>
              <li>Não apaga mensagens de outros membros. Ou seja, não modera golpe.</li>
              <li>O número ligado à API sai do aplicativo comum: não dá para usá-lo no celular ao mesmo tempo.</li>
              <li>Mais acesso no Gerenciador de Negócios da Meta não muda isso: é limite do produto, não de permissão.</li>
              <li>Onde ela serve: mandar alertas oficiais à equipe e, no futuro, atendimento. Podemos ligá-la depois sem refazer nada.</li>
            </ul>
          </Card>
        </Sec>

        <Sec t="Custos" sub="Valores aproximados de outubro/2026; conferir na contratação.">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground"><th className="p-2">Item</th><th className="p-2">O que é</th><th className="p-2">Custo</th><th className="p-2">Modera grupo?</th></tr>
              </thead>
              <tbody>
                {[
                  ["Radar, painel, regras (FEO Guard)", "Já feito no nosso site", "R$ 0", "—"],
                  ["Chip do robô", "Número pré-pago dedicado", "≈ R$ 15–30/mês", "—"],
                  ["Z-API (escolhido)", "Serviço brasileiro que mantém o número conectado 24h por QR Code. É ferramenta externa, mas só faz a ponte: as regras e decisões são nossas.", "≈ R$ 100/mês por número", "Sim: apaga e remove"],
                  ["Evolution API", "Mesma função do Z-API, gratuita, mas precisa de um servidor próprio ligado 24h e manutenção técnica.", "≈ R$ 30–60/mês de servidor", "Sim"],
                  ["WhatsApp Cloud API (Meta oficial)", "Ligação direta com a Meta pela conta empresarial.", "Receber: grátis. Mensagens iniciadas pela empresa: cobradas por mensagem (centavos de dólar cada)", "Não"],
                  ["SendFlow e similares", "Ferramenta pronta de terceiros", "Assinatura mensal", "Sim, nas regras deles"],
                ].map(([a, b, c, d]) => (
                  <tr key={a} className="border-t border-border"><td className="p-2 font-semibold">{a}</td><td className="p-2 text-muted-foreground">{b}</td><td className="p-2">{c}</td><td className="p-2">{d}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Total recomendado: ≈ R$ 115–130/mês (chip + Z-API). Risco: Z-API e Evolution são conexões não oficiais; o WhatsApp pode bloquear o número do robô. Por isso ele é exclusivo e descartável.
          </p>
        </Sec>

        <Sec t="Cronograma">
          <Card>
            <ol className="list-decimal space-y-1 pl-5 text-sm">
              <li><b>Hoje:</b> publicar o site (radar e painel entram no ar).</li>
              <li><b>Hoje/amanhã:</b> chip + conta Z-API + QR Code + robô admin dos grupos (≈ 30 min).</li>
              <li><b>Teste:</b> número comum manda link falso num grupo de teste; conferir apagar, remover e alerta.</li>
              <li><b>Antes de cada disparo:</b> checklist abaixo.</li>
            </ol>
            <p className="mt-2 text-xs text-muted-foreground">
              Limite: nenhum robô lê o privado dos alunos (criptografia do WhatsApp). Lá, a defesa é o aviso fixado e o plantão.
            </p>
          </Card>
        </Sec>

        <Sec t="Textos prontos">
          <div className="grid gap-3 md:grid-cols-2">
            <Copiar titulo="Alerta no grupo" texto={ALERTA_GRUPO} />
            <Copiar titulo="Aviso fixado / descrição" texto={AVISO_FIXO} />
            <Copiar titulo="Resposta a quem foi abordado" texto={RESP_PRIVADO} />
            <Copiar titulo="Para quem já pagou" texto={RESP_PAGOU} />
          </div>
        </Sec>

        <Sec t="Checklist antes de cada disparo">
          <Card>
            <ul className="space-y-1 text-sm">
              {["Grupo em apenas admins", "Admins revisados", "Aviso fixado", "Plantão do turno avisado", "Link oficial conferido"].map((i) => (
                <li key={i}>☐ {i}</li>
              ))}
            </ul>
          </Card>
        </Sec>
      </div>
    </main>
  );
}
